package com.example.mystybag.appCore.views

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.example.mystybag.appCore.logic.pages.NotificationSettingsViewModel
import com.example.mystybag.appCore.model.NotificationSettingToggle
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.SwitchButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.ui.views.UIView
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width

@Composable
fun NotificationSettingsView(viewModel: NotificationSettingsViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    // Wide desktop layout only on landscape Web; portrait Web + native use the mobile layout.
    val isWeb = rememberIsWebDesktopLayout()

    UIView(
        modifier = if (isWeb) {
            Modifier.fillMaxSize()
        } else {
            Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
        },
        viewModel = viewModel,
        showTopBar = !isWeb,
        removeVerticalPadding = false,
        navigationContent = if (!isWeb) {
            {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = p16),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(LocalTheme.p48)
                            .clip(CircleShape)
                            .background(AppTheme.current.colors.surfaceContainerLowest)
                            .noRippleClickable {
                                keyboardController?.hide()
                                viewModel.navCallbacks.backButtonCallback()
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(SRC.images.arrow_back),
                            contentDescription = null,
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.size(width = 10.dp, height = 18.dp),
                            colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
                        )
                    }

                    Texts.TitleMedium(
                        text = sharedString(SR.strings.notifications),
                        modifier = Modifier.weight(1f),
                        textAlign = TextAlign.Center,
                        color = AppTheme.current.colors.onSurface
                    )

                    Spacer(modifier = Modifier.size(LocalTheme.p48))
                }
            }
        } else null
    ) { uiState ->
        if (isWeb) {
            Column(modifier = Modifier.fillMaxSize()) {
                ClientWebHeaderBar(
                    selectedTab = 3,
                    homeTabIndex = 0,
                    deliveryTabIndex = 1,
                    favoritesTabIndex = 2,
                    menuTabIndex = 3,
                    searchCallbacks = null,
                    onFilterClick = {},
                    showSearchAndFilter = false,
                    showBackButton = true,
                    onBackClick = viewModel::onBackClick,
                    centerTitle = sharedString(SR.strings.notifications)
                )
                WebHeaderBottomDivider()
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.TopCenter
                ) {
                    NotificationSettingsContent(
                        uiState = uiState,
                        viewModel = viewModel,
                        modifier = Modifier
                            .width(1080.dp)
                            .verticalScroll(rememberScrollState())
                            .padding(horizontal = p16, vertical = p32)
                    )
                }
            }
        } else {
            NotificationSettingsContent(
                uiState = uiState,
                viewModel = viewModel,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16, vertical = p32)
            )
        }
    }
}

@Composable
private fun NotificationSettingsContent(
    uiState: androidx.compose.runtime.State<com.example.mystybag.appCore.model.NotificationSettingsUIState>,
    viewModel: NotificationSettingsViewModel,
    modifier: Modifier
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(0.dp)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p12)
        ) {
            Texts.LabelLarge(
                text = sharedString(SR.strings.reservation_completed).uppercase(),
                color = AppTheme.current.colors.onSurfaceVariant,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(LocalTheme.p16)
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(p2)
                    .background(AppTheme.current.colors.onSurfaceVariant.copy(alpha = 0.4f))
            )
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 24.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Texts.TitleMedium(
                text = sharedString(SR.strings.push),
                color = AppTheme.current.colors.onSurface,
                modifier = Modifier.weight(1f)
            )
            SwitchButton(
                modifier = Modifier.padding(start = p16),
                checked = uiState.value.reservationCompletedPushEnabled,
                onStateChange = { viewModel.onNotificationToggleChanged(NotificationSettingToggle.ReservationCompletedPush, it) }
            )
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 24.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Texts.TitleMedium(
                text = sharedString(SR.strings.email),
                color = AppTheme.current.colors.onSurface,
                modifier = Modifier.weight(1f)
            )
            SwitchButton(
                modifier = Modifier.padding(start = p16),
                checked = uiState.value.reservationCompletedEmailEnabled,
                onStateChange = { viewModel.onNotificationToggleChanged(NotificationSettingToggle.ReservationCompletedEmail, it) }
            )
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 24.dp),
            verticalArrangement = Arrangement.spacedBy(p12)
        ) {
            Texts.LabelLarge(
                text = sharedString(SR.strings.notifications_on_discounts_and_other_news).uppercase(),
                color = AppTheme.current.colors.onSurfaceVariant,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(LocalTheme.p16)
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(p2)
                    .background(AppTheme.current.colors.onSurfaceVariant.copy(alpha = 0.4f))
            )
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 24.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Texts.TitleMedium(
                text = sharedString(SR.strings.push),
                color = AppTheme.current.colors.onSurface,
                modifier = Modifier.weight(1f)
            )
            SwitchButton(
                modifier = Modifier.padding(start = p16),
                checked = uiState.value.discountsAndNewsPushEnabled,
                onStateChange = { viewModel.onNotificationToggleChanged(NotificationSettingToggle.DiscountsAndNewsPush, it) }
            )
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 24.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Texts.TitleMedium(
                text = sharedString(SR.strings.email),
                color = AppTheme.current.colors.onSurface,
                modifier = Modifier.weight(1f)
            )
            SwitchButton(
                modifier = Modifier.padding(start = p16),
                checked = uiState.value.discountsAndNewsEmailEnabled,
                onStateChange = { viewModel.onNotificationToggleChanged(NotificationSettingToggle.DiscountsAndNewsEmail, it) }
            )
        }
    }
}
